import { Box, List, Skeleton } from "@mui/material";
import { useMatchRoute } from "@tanstack/react-router";
import { useReferralConfig } from "@/components/cards/ReferralCards";
import { useHasVerifiedProxyPlan } from "@/hooks/useHasVerifiedProxyPlan";
import { RAIL_WIDTH } from "@/providers/DrawerProvider";
import { useAppPathname } from "@/routing/navigation";
import { PlanType } from "@/types/PlanTypes";
import WebshareLogo from "../../common/WebshareLogo";
import MenuIcon from "../NavMenu/MenuIcon";
import type { NavItemTarget, NavItemType } from "../NavMenu/types";
import checkIfItemSelected, {
  type MatchRoute,
} from "../NavMenu/utils/checkIfItemSelected";
import useNavPlans from "../NavMenuMultiPlan/hooks/useNavPlans";
import {
  INTEGRATION_ITEMS,
  isPlanSectionActive,
  PLANS_PRICING_ALTERNATIVES,
  PLANS_PRICING_TARGET,
  planPages,
  SUBSCRIPTION_ITEMS,
  type PlanKey,
} from "../NavMenuMultiPlan/navItems";
import RailItem, { RAIL_ITEM_SIZE } from "./RailItem";

/** The first internal page of a group: what its rail icon opens. */
function firstPage(items: NavItemType[]): NavItemTarget {
  const first = items.find((item) => !item.external);
  if (!first || first.external) {
    throw new Error("A rail group needs an internal page to open");
  }
  return { to: first.to, params: first.params };
}

function isGroupSelected(matchRoute: MatchRoute, items: NavItemType[]) {
  return items.some((item) => checkIfItemSelected(matchRoute, item));
}

function RailDivider() {
  return (
    <Box
      sx={{
        width: RAIL_ITEM_SIZE,
        my: 2,
        borderTop: 1,
        borderColor: "divider",
      }}
    />
  );
}

function ProductRailItem({
  title,
  icon,
  plan,
  planKey,
}: {
  title: string;
  icon: string;
  plan: PlanType | undefined;
  planKey: PlanKey;
}) {
  const pathname = useAppPathname();
  return (
    <RailItem
      title={title}
      inactive={!plan}
      icon={<img alt="" src={icon} width={24} height={24} />}
      selected={isPlanSectionActive(pathname, plan, planKey)}
      target={firstPage(planPages(plan, planKey))}
      testId={`RailLink_${title.replace(/[ &]+/g, "_")}`}
    />
  );
}

/** The product icons' stand-in while the plans load, like the sidebar's rows. */
function ProductSkeletons() {
  return (
    <>
      {[0, 1, 2, 3].map((index) => (
        <Box
          key={index}
          sx={{
            width: RAIL_ITEM_SIZE,
            height: RAIL_ITEM_SIZE,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Skeleton variant="rounded" width={24} height={24} />
        </Box>
      ))}
    </>
  );
}

/**
 * The sidebar collapsed to a 72px icon rail (R-2295): the same items in the
 * same order as the full sidebar, as 48px icon buttons. Labels, the PRODUCTS
 * title, chevrons, NEW chips, sub-pages and the Chrome extension card are
 * hidden; the logo becomes the symbol and still opens the Dashboard; the
 * dividers stay. A product's or group's icon opens its first page and never
 * expands the sidebar.
 */
export default function SidebarRail() {
  const pathname = useAppPathname();
  const matchRoute = useMatchRoute();
  const { planByType, isLoadingPlans } = useNavPlans();
  const hasVerifiedPlan = useHasVerifiedProxyPlan();
  const { data: config } = useReferralConfig();
  const isAffiliate = config?.mode === "payout";

  const plansAndPricingSelected =
    !!matchRoute({ to: PLANS_PRICING_TARGET.to }) ||
    PLANS_PRICING_ALTERNATIVES.some((target) => !!matchRoute(target));
  const subscriptionSelected = isGroupSelected(matchRoute, SUBSCRIPTION_ITEMS);
  const integrationsSelected = isGroupSelected(matchRoute, INTEGRATION_ITEMS);

  return (
    <Box
      component="nav"
      aria-label="Sidebar"
      data-testid="sidebar-rail"
      sx={{
        width: RAIL_WIDTH,
        boxSizing: "border-box",
        py: 3,
        px: 2.5,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 4,
        flex: 1,
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      <Box
        sx={{
          width: RAIL_ITEM_SIZE,
          height: 40,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <WebshareLogo variant="symbol" to="/dashboard" />
      </Box>
      <List
        disablePadding
        sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}
      >
        <RailItem
          title="Dashboard"
          icon={
            <MenuIcon icon="dashboard" selected={pathname === "/dashboard"} />
          }
          selected={pathname === "/dashboard"}
          target={{ to: "/dashboard" }}
          testId="RailLink_Dashboard"
        />
        {isLoadingPlans ? (
          <ProductSkeletons />
        ) : (
          <>
            {!!planByType.free && (
              <ProductRailItem
                title="Free"
                icon="/free-icon.svg"
                plan={planByType.free}
                planKey="free"
              />
            )}
            <ProductRailItem
              title="Proxy Server"
              icon="/datacenter-icon.svg"
              plan={planByType.datacenter}
              planKey="datacenter"
            />
            <ProductRailItem
              title="Static Residential"
              icon="/static-residential-icon.svg"
              plan={planByType.staticResidential}
              planKey="staticResidential"
            />
            <ProductRailItem
              title="Rotating Residential"
              icon="/residential-icon.svg"
              plan={planByType.residential}
              planKey="residential"
            />
            {hasVerifiedPlan && (
              <ProductRailItem
                title="Verified"
                icon="/verified-icon.svg"
                plan={planByType.verified}
                planKey="verified"
              />
            )}
          </>
        )}
        <RailDivider />
        <RailItem
          title="Plans & Pricing"
          icon={
            <MenuIcon
              icon="pricing"
              selected={pathname.startsWith("/subscription")}
            />
          }
          selected={plansAndPricingSelected}
          target={PLANS_PRICING_TARGET}
          testId="RailLink_Plans_Pricing"
        />
        <RailDivider />
        <RailItem
          title="Proxy Tester"
          icon={
            <MenuIcon icon="Server" selected={pathname === "/proxy-tester"} />
          }
          selected={pathname === "/proxy-tester"}
          target={{ to: "/proxy-tester" }}
          testId="RailLink_Proxy_Tester"
        />
        <RailItem
          title="Subscription"
          icon={
            <MenuIcon icon="Subscription" selected={subscriptionSelected} />
          }
          selected={subscriptionSelected}
          target={firstPage(SUBSCRIPTION_ITEMS)}
          testId="RailLink_Subscription"
        />
        <RailItem
          title={isAffiliate ? "Affiliate" : "Referral"}
          icon={
            <MenuIcon
              icon="Affiliate"
              selected={pathname === "/earn/referral"}
            />
          }
          selected={pathname === "/earn/referral"}
          target={{ to: "/earn/referral" }}
          testId="RailLink_Referral"
        />
        <RailItem
          title="Integrations"
          icon={
            <MenuIcon icon="Integrations" selected={integrationsSelected} />
          }
          selected={integrationsSelected}
          target={firstPage(INTEGRATION_ITEMS)}
          testId="RailLink_Integrations"
        />
      </List>
    </Box>
  );
}
